import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, CheckSquare, Square, MinusSquare } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const formatDate = (dateString, language) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(language, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const AffectationsTableCard = ({
  loading, employees,
  selectedEmployees, selectAll,
  handleSelectAll, handleSelectEmployee,
  currentPage, setCurrentPage,
  totalEmployees, limit,
}) => {
  const { t, i18n } = useTranslation();
  const selectionIcon = selectAll
    ? <CheckSquare className="h-5 w-5" />
    : selectedEmployees.length > 0
      ? <MinusSquare className="h-5 w-5" />
      : <Square className="h-5 w-5" />;

  return (
    <>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-foreground">{t('holidayAssignments.table.noEmployeeTitle')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t('holidayAssignments.table.noEmployeeDesc')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Button variant="ghost" size="sm" onClick={handleSelectAll} className="p-0">
                      {selectionIcon}
                    </Button>
                  </TableHead>
                  <TableHead>{t('holidayAssignments.table.registrationNumber')}</TableHead>
                  <TableHead>{t('holidayAssignments.table.fullName')}</TableHead>
                  <TableHead>{t('holidayAssignments.table.department')}</TableHead>
                  <TableHead>{t('holidayAssignments.filters.site')}</TableHead>
                  <TableHead>{t('holidayAssignments.table.currentCalendar')}</TableHead>
                  <TableHead>{t('holidayAssignments.table.assignedAt')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className={selectedEmployees.includes(employee.id) ? 'bg-primary/5' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedEmployees.includes(employee.id)}
                        onCheckedChange={() => handleSelectEmployee(employee.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{employee.registration_number}</TableCell>
                    <TableCell>{employee.first_name} {employee.last_name}</TableCell>
                    <TableCell>{employee.department?.name || '-'}</TableCell>
                    <TableCell>{employee.site?.name || '-'}</TableCell>
                    <TableCell>
                      {employee.current_calendar ? (
                        <Badge variant="secondary">{employee.current_calendar.name}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">{t('common.none')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {employee.current_calendar ? formatDate(employee.current_calendar.assigned_at, i18n.language) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalEmployees > limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {t('holidayAssignments.table.pageSummary', { page: currentPage, total: totalEmployees })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t('common.previous')}
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={employees.length < limit}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AffectationsTableCard;
